<template lang="html">
  <div class="fill-height">
    <Report 
      v-if="showReport && !error"
      :is-restricted="isRestricted"
      :report="report" 
      :loading="loader"
      :calculator-id="calculatorId" 
      :calculation-id="calculationId" 
      :sharing.sync="sharing"
    />

    <SiLoaderPopup
      :value="(!showReport || error) && loader"
      color="info"
      :label="loadingMessage"
    />

    <v-scale-transition>
      <v-container
        v-if="(!showReport || error) && !loader"
        grid-list-md
        fill-height
      >
        <v-layout
          justify-center
          align-center
          fill-height
        >
          <v-flex
            shrink
            style="width:400px"
          >
            <v-card>
              <v-card-title
                class="headline white--text"
                :class="[state]"
              >
                <v-icon
                  left
                  color="white"
                >
                  {{ state }}
                </v-icon>
                {{ title }}
              </v-card-title>
              <v-card-text
                class="pa-4"
                v-html="message"
              />
              <v-card-actions>
                <v-btn
                  v-if="generateLink"
                  text
                  color="grey"
                  @click="generateViewLink"
                >
                  <v-icon left>
                    link
                  </v-icon>Generate Link
                </v-btn>
                <v-btn
                  v-if="contact"
                  text
                  color="grey"
                  to="/contact"
                >
                  Contact Us
                </v-btn>
                <v-spacer />
                <v-btn
                  text
                  :color="state"
                  to="/"
                >
                  Take Me Home
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-flex>
        </v-layout>
      </v-container>
    </v-scale-transition>
    <SiFeedbackBar
      :value="feedback"
      for="calculator"
      :for-id="calculatorId"
      :meta="{calculatorId: calculatorId, calculationId: calculationId}"
      :save-path="calculationPath"
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import axios from 'axios'
import helpers from '@/mixins/helpers'
// import { rtdb } from '@/plugins/firebase'
import clientPath from '@/plugins/client-path'
import version from '@/version.json'

export default {
  mixins: [ helpers ],
  data () {
    return {
      loader: true,
      feedback: false,
      sharing: {},
      report: {},
      showReport: false,
      error: false,
      title: '',
      message: '',
      contact: false,
      state: 'info',
      generateLink: false,
      loadingMessage: 'Processing...',
      isRestricted: true,
      query: {}
    }
  },
  computed: {
    ...mapGetters(['_user']),
    calculatorId () {
      return this.$route.params.calculatorId
    },
    calculationId () {
      return this.$route.params.calculationId
    },
    calculationPath () {
      return `calculations/${this.calculatorId}/${this.calculationId}`
    },
    version () {
      let { major, minor, build } = version
      return `${major}.${minor}.${build}`
    },
  },
  beforeMount () {
    this.loader = true
    this.query = this.$route.query
    this.$store.commit('set_reportIsAdmin', Boolean(this.$route.query.admin))
    this.$store.commit('set_reportAdminToken', this.$route.query.token || null)
    
    
    if (this.$route.query.generateLink) {
      this.generateViewLink()
    } else {
      this.retrieveReport()
    }
    if (process.env.NODE_ENV !== 'development') this.$router.replace({ query: null });

  },
  methods: {
    print () {
      window.print()
    },
     async generateViewLink () {
      try {
        clientPath.track(`generate_link`, { calculatorId: this.calculatorId, calculationId: this.calculationId })
  
        let { userAgent = '', webdriver = '' } = navigator
      try { userAgent = userAgent.replace(/[^\w\s()/.]/gi, '') } catch (error) { console.error(error)}
        let shareId = this.shareId ? this.shareId : this.$route.query.shareId || ''
        let shareQuery = shareId ? `&shareId=${shareId}` : ''
        let monitoringQuery = `&userAgent=${userAgent}&webdriver=${webdriver}&webVersion=${this.version}`
        let url = `${process.env.VUE_APP_API_URL}/generate-link/${this.calculatorId}/${this.calculationId}?ts=${Number(new Date())}${shareQuery}${monitoringQuery}`
        // add time into query string as the browser caches the CORS resopnes. This forces it to do a fresh OPTIONS.
        
        this.loader = true
        this.showReport = false
        this.loadingMessage = 'Generating your link now'
        this.generateLink = false
  
        await axios.get(url) 

        this.state = 'info'
        this.title = 'Link sent'
        this.message = 'An email containing a single-use link has been sent to the address associted with this report. </br> </br> Be sure to check your junk/spam folder if your email does not appear.'
        this.loader = false
            
      } catch (error) {
        switch ((error.response.data || {}).code) {
          case 'not_authorized':
            this.title = 'Not Authorized'
            this.state = 'error'
            this.contact = true
            this.message = 'You are not authorized to access this report. If this report was shared with you, your access may have been removed by the owner. If you are the owner of this report please use the link in the email we sent you, or contact us for assistance. '
            break;
        
          default:
            this.title = `We've had an error`
            this.contact = true
            this.message = 'There was an error requesting your single-use link. Please try again and if the problem persistes contact us for assistance.'    
            break;
        }
        this.loader = false
        this.state = 'error'
        this.error = true
        console.error(error.response)
      }
    },
    retrieveReport () {
      clientPath.track(`view_report_${this.calculatorId}`, { calculatorId: this.calculatorId, calculationId: this.calculationId })

      this.loader = true
      let { userAgent = '', webdriver = '' } = navigator
      try { userAgent = userAgent.replace(/[^\w\s()/.]/gi, '') } catch (error) { console.error(error)}
      let navigatorQuery = [ `userAgent=${userAgent}`, `webdriver=${webdriver}`, `webVersion=${version}`]
      let query = [ ...navigatorQuery, `ts=${Number(new Date())}`,`uid=${this._user.uid}`, ...Object.keys(this.query).map(k => k + '=' + this.query[k])].join('&') || ''
      let url = `${process.env.VUE_APP_API_URL}/report/${this.calculatorId}/${this.calculationId}/retrieve`
      // add timestamp into query string as browser caches the CORS. Force it to do a fresh request. 
      this.showReport = true

      axios.get(`${url}?${query}`)
        .then(({ data = {} }) => {
          let { 
            _hasFeedback, 
            _unrestricted, 
            sharing = {}, 
            ...report 
            } = data || {}
          this.loader = false
          this.report = report
          this.sharing = sharing
          this.isRestricted = !_unrestricted

          if (!_hasFeedback && !this.query.admin && !sharing.shareId) setTimeout(() => { this.feedback = true }, 2000)
        })
        .catch(error => {
        // handle error
          let { data = {}, code } = error.response.data || {}
          this.error = true
          this.state = 'warning'
          this.shareId = data.shareId

          switch (code) {
            case 'not_found':
              this.title = 'Report does not exist'
              this.contact = true
              this.message = 'We were unable to find the report you have requested. If you belive this is an error please contact us for assistance.'
              break
            case 'not_paid':
              this.title = 'Report does not exist'
              this.message = 'The report you are requesting was never purchased/finalized. Please run another calculation and finalize your transaction'
              break
            case 'not_authorized':
              this.title = 'Not Authorized'
              this.state = 'error'
              this.contact = true
              this.message = 'You are not authorized to access this report. If this report was shared with you, your access may have been removed by the owner. If you are the owner of this report please use the link in the email we sent you, or contact us for assistance. '
              break
            case 'admin_expired':
              this.title = 'Admin authentication expired'
              this.message = 'Your authentication is too old. Please close this page, refresh your console and try again.'
              break
            case 'token_expired':
              this.title = 'Link expired'
              this.message = 'The link you are trying to use has expired. For your security single-use links only last for a short period of time. You can request a new link that will be sent to the email associated with this report.'
              this.generateLink = true
              break
            case 'token_used':
              this.title = 'Link expired'
              this.message = 'This link has already been used. You can request a new link that will be sent to the email associated with this report.'
              this.generateLink = true
              break
            case 'invalid_token':
              this.title = 'Invalid token'
              this.message = 'Invalid single-use token supplied. Please ensure you are only using the link in the email we sent you. If you copied and pasted the link ensure you copied the entire link. Please contact us if you continue to experience issues.'
              this.contact = true
              break
            case 'error':
              this.state = 'error'
              this.contact = true
              this.title = 'Error occured'
              this.message = 'There was a system erroy trying to retrieve your report. Please try again and if the probelem persists contact us for assistance.'
              break
            default:
              this.title = 'Error occured'
              this.state = 'error'
              this.contact = true
              this.message = 'An unknown error has occured. Please try again and if the problem persists contact us for assistance.'
          }
          this.loader = false
        })
    }
  }
}
</script>

<style lang="css">

</style>
